import { useState, useEffect } from "react";
import { Menu, X, User, LogIn } from "lucide-react";
import NexaticeIcon from "../assets/nexatice-icon.svg";
import { useAuth } from "../lib/auth";

interface NavbarProps {
  currentPage?: string;
}

export function Navbar({ currentPage = "home" }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  let auth: ReturnType<typeof useAuth> | null = null;
  try {
    auth = useAuth();
  } catch {
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks =
    currentPage === "home"
      ? [
          { href: "#features", label: "Features" },
          { href: "#databases", label: "Databases" },
          { href: "#pricing", label: "Pricing" },
          { href: "#download", label: "Download" },
        ]
      : currentPage === "dashboard"
      ? [
          { href: "/", label: "Home" },
          { href: "/docs", label: "Docs" },
          { href: "/support", label: "Support" },
        ]
      : [
          { href: "/", label: "Home" },
          { href: "/#features", label: "Features" },
          { href: "/#pricing", label: "Pricing" },
          { href: "/#download", label: "Download" },
        ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "navbar-glass py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3 group">
          <img src={NexaticeIcon} alt="Nexatice" className="w-6 h-6" />
          <span className="text-xl font-bold text-white">Nexatice</span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="nav-link text-sm font-medium text-zinc-400 hover:text-white transition-colors"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {auth?.isAuthenticated ? (
            <a
              href="/dashboard"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors"
            >
              <User className="w-4 h-4" />
              Dashboard
            </a>
          ) : (
            <a
              href="/login"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors"
            >
              <LogIn className="w-4 h-4" />
              Sign In
            </a>
          )}
          <a
            href={currentPage === "home" ? "#download" : "/#download"}
            className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-sm font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25 hover:-translate-y-0.5"
          >
            Download
          </a>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 text-zinc-400 hover:text-white transition-colors"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden navbar-glass mt-2 mx-4 rounded-xl overflow-hidden animate-fade-in">
          <div className="p-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium text-zinc-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-3 border-t border-white/10 space-y-2">
              {auth?.isAuthenticated ? (
                <a
                  href="/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-sm font-medium text-zinc-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  Dashboard
                </a>
              ) : (
                <a
                  href="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-sm font-medium text-zinc-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  Sign In
                </a>
              )}
              <a
                href={currentPage === "home" ? "#download" : "/#download"}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold rounded-lg text-center transition-colors"
              >
                Download
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
